export type LineGuideActionType = 'uri' | 'message' | 'image' | 'liff';
export type LineGuideStyle = 'call' | 'amber' | 'navy';
export type LineGuideReplyType = 'image' | 'text' | 'uri' | 'liff';

export const LINE_GUIDE_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const LINE_GUIDE_PICTURE_BASE = 'https://pss-upload.cloud/Parkingcaptures/';

export interface LineGuideItem {
  id: number;
  /** ใช้ trackBy ในฟอร์มเท่านั้น — ไม่ส่งขึ้น API */
  uiKey?: string;
  sortOrder: number;
  label: string;
  actionType: LineGuideActionType;
  actionValue: string;
  style: LineGuideStyle;
  guideKey?: string | null;
  isActive: boolean;
}

export interface LineGuideMenu {
  id: number;
  key: string;
  triggerText: string;
  titleTh: string;
  subtitleTh?: string | null;
  altText?: string | null;
  isActive: boolean;
  items: LineGuideItem[];
}

export interface LineGuideContent {
  id: number;
  /** ใช้ trackBy ในฟอร์มเท่านั้น — ไม่ส่งขึ้น API */
  uiKey?: string;
  matchKey: string;
  matchText: string;
  replyType: LineGuideReplyType;
  imageUrl?: string | null;
  textBody?: string | null;
  isActive: boolean;
  /** แนบรอก่อนบันทึก — ไม่ส่ง API */
  pendingBase64?: string | null;
  pendingFileName?: string | null;
}

export interface LineGuideConfigResponse {
  menu?: LineGuideMenu | null;
  Menu?: LineGuideMenu | null;
  contents?: LineGuideContent[];
  Contents?: LineGuideContent[];
  actionTypes?: string[];
  styles?: string[];
  replyTypes?: string[];
}

export interface LineGuideConfigSavePayload {
  userId: number;
  menuId: number;
  menu: LineGuideMenu;
  contents: LineGuideContent[];
}

export const LINE_GUIDE_ACTION_OPTIONS: { value: LineGuideActionType; label: string }[] = [
  { value: 'uri', label: 'ลิงก์ / โทร (uri)' },
  { value: 'message', label: 'ส่งข้อความ → คู่มือ' },
  { value: 'image', label: 'ส่งข้อความ → รูป (image)' },
  { value: 'liff', label: 'เปิด LIFF' },
];

export const LINE_GUIDE_STYLE_OPTIONS: { value: LineGuideStyle; label: string }[] = [
  { value: 'call', label: 'โทร / รอง' },
  { value: 'amber', label: 'CTA เหลือง' },
  { value: 'navy', label: 'มาตรฐาน Navy' },
];

export const LINE_GUIDE_REPLY_OPTIONS: { value: LineGuideReplyType; label: string }[] = [
  { value: 'image', label: 'ส่งรูปในแชท' },
  { value: 'text', label: 'ข้อความ' },
  { value: 'uri', label: 'เปิดเว็บ (ลิงก์)' },
  { value: 'liff', label: 'เปิด LIFF' },
];

export function resolveLineGuideImageUrl(pathOrUrl?: string | null): string {
  if (!pathOrUrl?.trim()) return 'img/notfound.jpg';
  const value = pathOrUrl.trim();
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return `${LINE_GUIDE_PICTURE_BASE}${value.replace(/^\//, '')}`;
}

let lineGuideUiSeq = 0;

export function nextLineGuideUiKey(prefix: string): string {
  lineGuideUiSeq += 1;
  return `${prefix}-${Date.now()}-${lineGuideUiSeq}`;
}

export function createEmptyLineGuideMenu(): LineGuideMenu {
  return {
    id: 0,
    key: 'help_menu',
    triggerText: 'ติดต่อสอบถาม/แจ้งปัญหา',
    titleTh: 'ศูนย์ช่วยเหลือ',
    subtitleTh: 'เลือกหัวข้อที่ต้องการได้จากด้านล่าง',
    altText: 'เมนูช่วยเหลือ City Parking',
    isActive: true,
    items: [],
  };
}

export function createEmptyLineGuideItem(sortOrder = 10): LineGuideItem {
  return {
    id: 0,
    uiKey: nextLineGuideUiKey('item'),
    sortOrder,
    label: '',
    actionType: 'message',
    actionValue: '',
    style: 'navy',
    guideKey: '',
    isActive: true,
  };
}

export function createEmptyLineGuideContent(): LineGuideContent {
  return {
    id: 0,
    uiKey: nextLineGuideUiKey('content'),
    matchKey: '',
    matchText: '',
    replyType: 'image',
    imageUrl: '',
    textBody: '',
    isActive: true,
    pendingBase64: null,
    pendingFileName: null,
  };
}

export function mapLineGuideConfigResponse(dto: LineGuideConfigResponse): {
  menu: LineGuideMenu;
  contents: LineGuideContent[];
} {
  const rawMenu = dto.menu ?? dto.Menu;
  const rawContents = dto.contents ?? dto.Contents ?? [];
  const menu = rawMenu
    ? {
        id: rawMenu.id ?? (rawMenu as any).Id ?? 0,
        key: rawMenu.key ?? (rawMenu as any).Key ?? 'help_menu',
        triggerText: rawMenu.triggerText ?? (rawMenu as any).TriggerText ?? '',
        titleTh: rawMenu.titleTh ?? (rawMenu as any).TitleTh ?? '',
        subtitleTh: rawMenu.subtitleTh ?? (rawMenu as any).SubtitleTh ?? '',
        altText: rawMenu.altText ?? (rawMenu as any).AltText ?? '',
        isActive: rawMenu.isActive ?? (rawMenu as any).IsActive ?? true,
        items: (rawMenu.items ?? (rawMenu as any).Items ?? []).map((i: any) => ({
          id: i.id ?? i.Id ?? 0,
          uiKey: nextLineGuideUiKey(`item-${i.id ?? i.Id ?? 0}`),
          sortOrder: i.sortOrder ?? i.SortOrder ?? 0,
          label: i.label ?? i.Label ?? '',
          actionType: (i.actionType ?? i.ActionType ?? 'message') as LineGuideActionType,
          actionValue: i.actionValue ?? i.ActionValue ?? '',
          style: (i.style ?? i.Style ?? 'navy') as LineGuideStyle,
          guideKey: i.guideKey ?? i.GuideKey ?? '',
          isActive: i.isActive ?? i.IsActive ?? true,
        })),
      }
    : createEmptyLineGuideMenu();

  const contents = rawContents.map((c: any) => ({
    id: c.id ?? c.Id ?? 0,
    uiKey: nextLineGuideUiKey(`content-${c.id ?? c.Id ?? 0}`),
    matchKey: c.matchKey ?? c.MatchKey ?? '',
    matchText: c.matchText ?? c.MatchText ?? '',
    replyType: (c.replyType ?? c.ReplyType ?? 'image') as LineGuideReplyType,
    imageUrl: c.imageUrl ?? c.ImageUrl ?? '',
    textBody: c.textBody ?? c.TextBody ?? '',
    isActive: c.isActive ?? c.IsActive ?? true,
    pendingBase64: null,
    pendingFileName: null,
  }));

  return { menu, contents };
}

/** ตัด uiKey / pending ก่อนส่ง API */
export function toLineGuideSavePayload(
  menu: LineGuideMenu,
  contents: LineGuideContent[],
  userId: number,
  menuId: number,
): LineGuideConfigSavePayload {
  return {
    userId,
    menuId,
    menu: {
      id: menu.id,
      key: menu.key,
      triggerText: menu.triggerText,
      titleTh: menu.titleTh,
      subtitleTh: menu.subtitleTh,
      altText: menu.altText,
      isActive: menu.isActive,
      items: menu.items.map(({ uiKey: _uiKey, ...item }) => item),
    },
    contents: contents.map(
      ({ uiKey: _uiKey, pendingBase64: _b64, pendingFileName: _fn, ...c }) => c,
    ),
  };
}
